/**
 * Reference
 * https://www.grammarcheck.net/capitalization-in-titles-101/
 */
const _articles = ['a', 'an', 'the'];
// prettier-ignore
const _conjunctions = [
  'and', 'as', 'but', 'for', 'if', 'nor', 'once', 'or', 'so', 'than', 'that', 'till', 'when', 'yet'
];
// prettier-ignore
const _prepositions = [
  'at', 'by', 'down', 'for', 'from', 'in', 'into', 'like', 'near', 'of', 'off', 'on', 'onto', 'over', 'past', 'to',
  'upon', 'with'
];
const _exceptions = [..._articles, ..._conjunctions, ..._prepositions];

const capWord = (text: string) =>
  text.replace(/(?:^|\s|-|\/)\S/g, (match: string) => match.toUpperCase());
const capWords = (word: string, index: number, words: string[]) =>
  (words[index] = capWord(word));
const capWordsWithExceptions = (
  word: string,
  index: number,
  words: string[],
  lowercase: RegExp
) => {
  if (lowercase?.test(word)) {
    words[index] = word.replace(lowercase, (match: string) =>
      match.toLowerCase()
    );

    return;
  }

  return capWords(word, index, words);
};

interface TitleizeOptions {
  /**
   * Allows to disable capitalization of certain words.
   * true to use default exceptions list or strings array with exceptions
   */
  lowercase?: boolean | string[];
  /** List of words to be all capitals */
  uppercase?: string[];
  /** Skip titleize if the text already contains capital letters */
  validateCaps?: boolean;
}

/**
 * Titleize with options to avoid capitalization on specific words, uppercase specific words and skip formatting
 * if the text already contains capital letters
 * @param {string} input Text to titleize
 * @param {Object} options Titleization options
 * @param {boolean|string[]} options.lowercase Allows to disable capitalization of certain words.
 *   true to use default exceptions list or strings array with exceptions
 * @param {string[]} options.uppercase List of words to be all capitals
 * @param {boolean} options.validateCaps Skip titleize if the text already contains capital letters
 * @returns Titleized string
 * @example
 * import { titleize } from '@/utils/titleize';
 *
 * // basicTitleized = 'Should Update All Words, Even The Default Exceptions Like A And From';
 * const basicTitleized = titleize('should update all words, even the default exceptions like a and from');
 */
export function titleize(input: string, options?: TitleizeOptions) {
  if (!input) return '';

  const defaults = { lowercase: false, uppercase: [], validateCaps: true };
  options = { ...defaults, ...options };

  const lowercaseWords = parseLowercaseOptions(options);
  const uppercaseWords = Array.isArray(options.uppercase)
    ? options.uppercase
    : [];
  const searchExp = (list: string[]) =>
    new RegExp(`\\b(?:${list.join('|')})\\b`, 'gi');
  const skip = options.validateCaps && /[A-Z]/.test(input);

  if (!skip) {
    const words = input.split(' ');

    if (lowercaseWords.length) {
      words.forEach((word: string, index: number, words: string[]) => {
        const lowercase = searchExp(lowercaseWords);
        capWordsWithExceptions(word, index, words, lowercase);
      });
    } else {
      words.forEach(capWords);
    }

    input = words.join(' ');
  }

  // Even if caps validation is enabled the uppercase list needs to be applied
  if (uppercaseWords.length) {
    input = input.replace(
      searchExp(uppercaseWords),
      (match) =>
        uppercaseWords.find(
          (word: string) => word.toLowerCase() === match.toLowerCase()
        ) || ''
    );
  }

  return input;
}

function parseLowercaseOptions(options: TitleizeOptions): string[] {
  let exceptions: string[] = [];

  if (typeof options.lowercase === 'boolean' && options.lowercase) {
    exceptions = _exceptions;
  }

  if (Array.isArray(options.lowercase)) {
    exceptions = options.lowercase;
  }

  return exceptions;
}
